import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { MiniPlayer } from '../audio/MiniPlayer'
import { api } from '../lib/api'
import { useTheme } from '../lib/useTheme'
import { Icon } from './Icon'

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/theories', label: 'Theories', end: false },
  { to: '/story', label: 'Story', end: false },
]

export function Layout() {
  const { theme, toggleTheme } = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const [rolling, setRolling] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  const rollTheDice = async () => {
    if (rolling) return
    setRolling(true)
    try {
      // Pass the slug we're on so it never lands you where you already are.
      const onTheory = /^\/theory\/([^/?#]+)/.exec(location.pathname)
      const { slug } = await api.random(onTheory ? decodeURIComponent(onTheory[1]) : undefined)
      navigate(`/theory/${slug}`)
    } catch {
      // Nothing published yet, or the server is unreachable. The archive is a
      // more useful place to land than an error nobody can act on.
      navigate('/theories')
    } finally {
      setRolling(false)
    }
  }

  // Close the mobile menu whenever the route changes.
  useEffect(() => setMenuOpen(false), [location.pathname])

  // Scroll to top on navigation, but leave in-page anchor jumps alone.
  useEffect(() => {
    if (!location.hash) window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [location.pathname, location.hash])

  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <header className="site-header">
        <div className="page site-header-inner">
          <Link to="/" className="brand" aria-label="Shit Max Says — home">
            <span className="brand-mark" aria-hidden="true">
              M
            </span>
            <span className="brand-text">
              Shit Max <em>Says</em>
            </span>
          </Link>

          <button
            type="button"
            className="btn btn-subtle btn-icon nav-toggle"
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Icon name={menuOpen ? 'close' : 'menu'} />
            <span className="visually-hidden">Menu</span>
          </button>

          <nav id="site-nav" className={`site-nav${menuOpen ? ' is-open' : ''}`} aria-label="Main">
            <ul>
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>

            <div className="site-nav-actions">
              <button
                type="button"
                className={`btn btn-subtle btn-icon dice-button${rolling ? ' is-rolling' : ''}`}
                onClick={() => void rollTheDice()}
                disabled={rolling}
                aria-label="Read a random theory"
                title="Read a random theory"
              >
                <Icon name="dice" />
              </button>
              <button
                type="button"
                className="btn btn-subtle btn-icon"
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              >
                <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
              </button>
            </div>
          </nav>
        </div>
      </header>

      <main id="main" className="site-main">
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="page site-footer-inner">
          <p>
            <strong>Shit Max Says</strong> — an archive of theories that have not been peer
            reviewed, fact checked, or in most cases finished.
          </p>
          <nav aria-label="Footer">
            <ul className="cluster">
              <li>
                <Link to="/theories" className="footer-link">
                  Archive
                </Link>
              </li>
              <li>
                <Link to="/story" className="footer-link">
                  Story
                </Link>
              </li>
              <li>
                {/* A plain <a>: the feed is served by the server, not the router. */}
                <a href="/rss.xml" className="footer-link">
                  RSS
                </a>
              </li>
              <li>
                <Link to="/admin" className="footer-link footer-link-faint">
                  Admin
                </Link>
              </li>
            </ul>
          </nav>
          <p className="mono footer-fine">© {new Date().getFullYear()} · Verified nowhere</p>
        </div>
      </footer>

      <MiniPlayer />
    </div>
  )
}
